import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Layout } from "@/components/layout/Layout";
import { signInWithGoogle } from "@/lib/firebase";
import { Loader2 } from "lucide-react";

export default function FirebaseConfigTestPage() {
  const [firebaseConfig, setFirebaseConfig] = useState<any>(null);
  const [currentDomain, setCurrentDomain] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const domain = window.location.hostname;
      setCurrentDomain(domain);
      
      // Get Firebase config for display (don't display API key)
      setFirebaseConfig({
        apiKey: "***REDACTED***",
        authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
        currentDomain: domain
      });
    }
  }, []);
  
  const testGoogleSignIn = async () => {
    setTestStatus("loading");
    setTestResult(null);
    setLoading(true);
    
    try {
      // Test Google sign-in
      const result = await signInWithGoogle(false);
      console.log("Sign-in result:", result);
      
      if (result && result.user) {
        setTestResult(`SUCCESS: Google Sign-in worked! 
          User: ${result.user.displayName || result.user.email}
          Email: ${result.user.email}
          UID: ${result.user.uid}`);
        setTestStatus("success");
      } else {
        setTestResult("ERROR: Sign-in completed but no user data returned");
        setTestStatus("error");
      }
    } catch (error: any) {
      console.error("Test failed:", error);
      
      let errorMessage = "";
      
      if (error.code === 'auth/configuration-not-found') {
        errorMessage = `ERROR: Firebase configuration not found for this domain.
        
This likely means that ${currentDomain} is not added to the list of Authorized Domains in your Firebase project.

To fix this:
1. Go to the Firebase Console
2. Select your project
3. Go to Authentication > Settings > Authorized Domains
4. Add ${currentDomain} to the list and save`;
      } else {
        errorMessage = `ERROR: ${error.message || "Unknown error"}
Code: ${error.code || "none"}`;
      }
      
      setTestResult(errorMessage);
      setTestStatus("error");
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusColor = () => {
    switch (testStatus) {
      case "success": return "bg-green-50 border-green-200 text-green-700";
      case "error": return "bg-red-50 border-red-200 text-red-700";
      default: return "bg-blue-50 border-blue-200 text-blue-700";
    }
  };
  
  return (
    <Layout>
      <div className="container py-12">
        <Card>
          <CardHeader>
            <CardTitle>Firebase Configuration Test</CardTitle>
            <CardDescription>
              This page tests your Firebase configuration to ensure Google Sign-in works properly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {firebaseConfig && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                <h3 className="font-medium mb-2">Current Firebase Configuration</h3>
                <pre className="text-sm whitespace-pre-wrap">
                  {JSON.stringify(firebaseConfig, null, 2)}
                </pre>
              </div>
            )}
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm">
              <p className="font-medium mb-2">Information</p>
              <p>Current domain: <strong>{currentDomain}</strong></p>
              <p className="mt-1">
                This domain must be added to the list of <strong>Authorized Domains</strong> in your Firebase project
                settings for Google Sign-in to work properly.
              </p>
            </div>
            
            {testResult && (
              <div className={`p-4 border rounded-md ${getStatusColor()}`}>
                <p className="font-medium mb-2">Test Result</p>
                <pre className="text-sm whitespace-pre-wrap">
                  {testResult}
                </pre>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button 
              onClick={testGoogleSignIn} 
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                "Test Google Sign-in"
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </Layout>
  );
}